'use client';

import React, { useEffect, useRef } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastProvider, useToast } from '@/app/admin/components/shared';
import { useAuthStore } from '@/stores/auth.store';
import { setupApiClientAuth } from '@/lib/api/client';
import { registerGlobalToast } from '@/lib/utils/global-toast';

function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Data is considered fresh for 60 seconds; background refetch after that.
        staleTime: 60_000,
        // Never retry on auth errors — redirect immediately.
        retry: (failureCount, error: unknown) => {
          const status = (error as { status?: number })?.status;
          if (status === 401 || status === 403) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: true,
      },
      mutations: {
        // Mutations never auto-retry — show the error so the user can decide.
        retry: false,
      },
    },
  });
}

// Registers the showToast function from the ToastContext as a global callback,
// so the axios interceptor can trigger toasts from outside the React tree.
function ToastRegistrar() {
  const { showToast } = useToast();
  useEffect(() => {
    registerGlobalToast(showToast);
  }, [showToast]);
  return null;
}

// Runs once on mount:
// 1. Wires the Zustand auth store into the axios interceptors.
// 2. Restores a session from localStorage if a valid refresh token exists.
function AuthInitializer({ children }: { children: React.ReactNode }) {
  const initialize = useAuthStore((s) => s.initialize);
  const getAccessToken = useAuthStore((s) => s.getAccessToken);
  const logout = useAuthStore((s) => s.logout);

  useEffect(() => {
    setupApiClientAuth(getAccessToken, () => {
      logout().then(() => {
        window.location.href = '/admin/login';
      });
    });
    initialize();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <>{children}</>;
}

// Root provider tree — added to src/app/layout.tsx.
// useRef prevents a new QueryClient being created on every HMR reload in dev.
export function AppProviders({ children }: { children: React.ReactNode }) {
  const clientRef = useRef<QueryClient | null>(null);
  if (!clientRef.current) {
    clientRef.current = makeQueryClient();
  }

  return (
    <QueryClientProvider client={clientRef.current}>
      <ToastProvider>
        <ToastRegistrar />
        <AuthInitializer>{children}</AuthInitializer>
      </ToastProvider>
    </QueryClientProvider>
  );
}
